<template>
  <section class="settings-page">
    <h1 class="forza-bold text-uppercase">Sales Tools Settings</h1>
    <!-- Welcome Message -->
    <div class="welcome-info" v-if="!hideExplainerText">
      <!-- <img src="@/assets/icons/ui/color-filter-outline-blue.svg" /> -->
      <div class="text">
        <p>
          <span class="slate-bold"
            >Welcome to your new Sales Tools Settings page!
          </span>
        </p>
        <p>
          <span>
            Fine-tune your branding by uploading a logo, choose what type type
            of pricing will be displayed and control who can edit your Custom
            Catalogs by setting up an access code.</span
          >
        </p>
      </div>
      <button @click="handleHideExplainerText()">
        <img src="@/assets/icons/ui/close-outline.png" alt="close button" />
      </button>
    </div>
    <section class="settings-container">
      <article class="branding-settings-container">
        <h2 class="slate-bold">Branding</h2>

        <div class="upload-logo-container">
          <p>
            Upload or edit the logo shown in Custom Catalogs that you share with
            customers.
          </p>

          <UploadLogoButton
            :type="'upload'"
            v-if="!dealerLogoImage"
            :uploadToThreekit="false"
          />
          <div v-else class="dealer-logo-container">
            <div class="logo-box">
              <img :src="dealerLogoImage" alt="dealer logo" />
            </div>
            <div class="logo-actions">
              <button
                v-if="dealer.logo_id"
                class="btn"
                @click="
                  handleEditLogo({
                    logoUrl: dealer.logo,
                    logoContent: dealer.logo_content,
                  })
                "
              >
                <img
                  src="~@/assets/icons/ui/pencil-outline-dark.svg"
                  alt="Edit product"
                />
              </button>
              <button class="btn" @click="deleteDealerLogo()">
                <img
                  src="~@/assets/icons/ui/trash-outline-pink.svg"
                  alt="Delete product"
                />
              </button>
            </div>
          </div>
        </div>
      </article>
      <article class="prices-settings-container">
        <h2 class="slate-bold">Preferences</h2>

        <div class="code-container">
          <p>Your Access Code</p>

          <span class="text-grey-dark"
            >Choose an Access Code so you can enter "Dealer Mode" and make
            adjustments to your Custom Catalogs.</span
          >

          <label for="passcode">
            <input
              :type="showPassword ? 'text' : 'password'"
              id="passcode"
              v-model="dealer.passcode"
              @input="(e) => changeDealerSettings(e.target.value, 'passcode')"
            />
            <div
              class="show-password-container"
              :class="{ show: showPassword }"
              @click="showPassword = !showPassword"
            >
              <img
                src="@/assets/icons/ui/eye-outline-black.svg"
                alt="show password"
              />
            </div>
          </label>
        </div>

        <div class="pricing-settings-container">
          <p>Pricing</p>
          <span class="text-grey-dark">
            Choose between showing MSRP or hide pricing completely.
          </span>
          <div class="checkboxes-container">
            <label for="show-msrp" class="switch-btn">
              <p class="label-text">
                <span class="slate-bold">Show MSRP for every product</span>
              </p>
              <input
                type="checkbox"
                id="show-msrp"
                :checked="
                  dealer && dealer.settings && dealer.settings.display_msrp
                "
                @input="
                  (e) =>
                    changeDealerSettings(
                      !dealer.settings.display_msrp,
                      'display_msrp',
                      true
                    )
                "
              />
              <div class="slide">
                {{
                  dealer && dealer.settings && dealer.settings.display_msrp
                    ? "On"
                    : "Off"
                }}
              </div>
            </label>
          </div>
        </div>
      </article>
    </section>
    <transition name="fade">
      <UploadLogosModal @saveLogo="saveDealerLogo" :createThreekitAsset="false"
    /></transition>
    <transition name="fade">
      <div class="tk-modal-loader" v-if="loading">
        <Loader :withMessage="true" :message="threekitLoaderMessage" />
      </div>
    </transition>
  </section>
</template>

<script>
// import axios from "axios";
import { mapActions, mapState } from "vuex";
import { debounce } from "debounce";
import { cloneDeep } from "lodash";
import UploadLogoButton from "@/components/Utils/Logos/UploadLogoButton.vue";
import UploadLogosModal from "@/components/Utils/Logos/UploadLogosModal";
import Loader from "@/components/Utils/Loader";
export default {
  name: "MySettings",
  components: { UploadLogoButton, UploadLogosModal, Loader },
  data() {
    return {
      hideExplainerText: window.localStorage.getItem(
        "gameplan-hide-explainer-text"
      ),
      showPassword: false,
      loading: false,
    };
  },
  computed: {
    ...mapState(["auth", "dealer"]),

    account_number() {
      return this.dealer.fsg_code;
    },

    hasLogo() {
      return this.dealer.logo;
    },

    dealerLogoImage() {
      return this.dealer.logo_content || this.dealer.logo;
    },
  },

  methods: {
    ...mapActions("dealer", ["deleteDealerLogo", "updateDealer"]),
    ...mapActions("editableLogo", ["DIGIrequestImageAsBse64", "setApiLoading"]),

    ...mapActions("uploadLogos", ["openEditLogoModalToEditSingleLogo"]),
    ...mapActions("project", ["init2DDecorationsApi"]),

    async saveDealerLogo({ logo }) {
      try {
        let formData = new FormData();
        formData.append("logo", logo?.url);
        formData.append("logo_content", logo?.content);
        formData.append("logo_id", logo && logo?.id);
        this.updateDealer(formData);
      } catch (error) {
        console.log(error);
      }
    },

    changeDealerSettings: debounce(async function (
      value,
      field_name,
      isCheckbox
    ) {
      if (value || isCheckbox) {
        try {
          this.loading = true;
          let formData = new FormData();
          formData.append(field_name, value);
          await this.updateDealer(formData);
        } catch (error) {
          console.log(error);
        } finally {
          this.loading = false;
        }
      }
    },
    750),

    handleHideExplainerText() {
      window.localStorage.setItem("gameplan-hide-explainer-text", true);
      this.hideExplainerText = true;
    },

    async handleEditLogo(logoUrl, logoContent) {
      try {
        this.setApiLoading(true);
        //Convert the url to a base64 string
        this.openEditLogoModalToEditSingleLogo();
        const base64 =
          logoContent || (await this.DIGIrequestImageAsBse64(logoUrl));
        const logoClone = cloneDeep({
          id: this.dealer.logo_id,
          logoId: this.dealer.logo_id,
          logo_id: this.dealer.logo_id,
          content: base64,
        });
        await this.openEditLogoModalToEditSingleLogo(logoClone);
      } catch (error) {
        console.log(error);
      } finally {
        this.setApiLoading(false);
      }
    },
  },

  mounted() {
    this.$store.commit("setActivePage", "My Settings");

    window.scrollTo({
      top: document.getElementById("kt_header").offsetTop,
      behavior: "smooth",
    });
  },
};
</script>
<style lang="scss" scoped>
.settings-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 1rem;
  h1 {
    font-size: 1.75rem;
    margin-bottom: 1rem;
  }
  .welcome-info {
    display: flex;
    background: #eef3ff;
    border-radius: 6px;
    border: 1px solid rgba(7, 81, 237, 0.5);
    margin-bottom: 1.5rem;
    padding: 1rem;
    img {
      margin-right: 1rem;
    }
    span {
      color: $showroomColor;
    }
    button {
      margin-left: auto;
      background: none;
    }
  }
  .settings-container {
    background: white;
    display: flex;
    flex-grow: 1;
    .branding-settings-container,
    .prices-settings-container {
      width: 50%;
      padding: 1.5rem 2rem;
      h2 {
        margin-bottom: 1.5rem;
      }
    }
    .branding-settings-container {
      border-right: 1px solid #ebebeb;
      .upload-logo-container {
        p {
          margin-bottom: 1.5rem;
        }

        ::v-deep .upload-logo,
        .dealer-logo-container .logo-box {
          max-width: 300px;
          min-width: 60%;
          height: 100px;
        }
        .dealer-logo-container {
          display: flex;
          .logo-box {
            background: $grey-lighter;
            border-radius: 6px;
            border: 1px solid $placeholder;
            margin-right: 1rem;
            padding: 0.25rem;
            img {
              display: block;
              max-height: 100%;
              max-width: 100%;
              margin: auto;
            }
          }
          .logo-actions {
            display: flex;
            flex-direction: column;
            justify-content: space-around;
            button {
              background: $grey-lightest;
            }
          }
        }
      }
    }
    .prices-settings-container {
      border-left: 1px solid #ebebeb;

      .code-container,
      .pricing-settings-container {
        margin-bottom: 3rem;

        & > p {
          margin-bottom: 1.5rem;
        }

        span {
          display: block;
          &:last-child() {
            margin-bottom: 1.75rem;
          }
        }

        label {
          display: flex;
          input {
            padding: 8px 8px 8px 12px;
            border: 1px solid $grey-dark;
            box-sizing: border-box;
            border-radius: 6px;
          }
        }
      }

      .code-container {
        label {
          display: flex;
          width: 60%;
          input {
            flex-direction: row;
            align-items: center;
            padding: 8px 8px 8px 12px;
            width: 100%;

            /* white */

            background: #ffffff;
            /* grey-dark */

            border: 1px solid $grey-dark;
            box-sizing: border-box;
            border-radius: 6px;
          }
          .show-password-container {
            display: flex;
            align-items: center;
            justify-content: center;
            margin-left: -35px;
            margin-bottom: auto;
            margin-top: auto;
            width: 30px;
            height: 30px;
            background: white;
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.2s;
            &:hover {
              border: 1px solid black;
            }
            &.show {
              filter: invert(1);
            }
          }
        }
      }

      .pricing-settings-container {
        .checkboxes-container {
          margin-top: 2rem;

          .switch-btn {
            position: relative;
            display: flex;
            flex-direction: column;
            input {
              display: none !important;
            }
          }
          .label-text {
            margin-bottom: 0.75rem;
          }
          .slide {
            display: flex;
            align-items: center;
            border-radius: 34px;
            cursor: pointer;
            background-color: #ccc;
            -webkit-transition: 0.4s;
            transition: 0.4s;
            padding: 8px;
            position: relative;
            height: 34px;
            color: #fff;
            width: 110px;
          }
          .slide:before {
            border-radius: 50%;
            position: absolute;
            content: "";
            height: 26px;
            width: 26px;
            left: 78px;
            bottom: 4px;
            background-color: white;
            -webkit-transition: 0.4s;
            transition: 0.4s;
          }
          input:checked + .slide {
            background-color: #059c37;
            padding-left: 40px;
          }
          input:focus + .slide {
            box-shadow: 0 0 1px #01aeed;
          }
          input:checked + .slide:before {
            -webkit-transform: translateX(26px);
            -ms-transform: translateX(26px);
            transform: translateX(26px);
            left: -20px;
          }
        }
      }
    }
  }

  .spinner-loader-container {
    ::v-deep .loading-spinner {
      margin-right: 1.5rem;
    }
  }
}
</style>
